import PropTypes from 'prop-types';
import { matchPath, useLocation, Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Tooltip } from '@mui/material';

// project-imports
// import IconButton from 'components/@extended/IconButton';

import useConfig from 'hooks/useConfig';
import { MenuOrientation, ThemeMode } from 'config';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| NAVIGATION - ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, isnew = false }) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { mode, menuOrientation } = useConfig();

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      variant="liner"
      size={drawerOpen ? 22 : 22}
      style={{ ...(menuOrientation === MenuOrientation.HORIZONTAL && isParents && { fontSize: 20, stroke: '1' }) }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url, end: false }, pathname);

  const iconSelectedColor = 'secondary.100';

  // Check if URL is external (starts with http:// or https://)
  const isExternalUrl = item.url && (item.url.startsWith('http://') || item.url.startsWith('https://'));
  
  // Handle external URL navigation
  const handleExternalClick = (e) => {
    if (isExternalUrl) {
      e.preventDefault();
      window.location.href = item.url;
    }
    if (downLG) {
      handlerDrawerOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative'}}>
        <ListItemButton
          component={isExternalUrl ? 'div' : Link}
          to={isExternalUrl ? undefined : item.url}
          href={isExternalUrl ? item.url : undefined}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          onClick={isExternalUrl ? handleExternalClick : (downLG ? () => handlerDrawerOpen(false) : undefined)}
          sx={{
            zIndex: 1201,
            pl: drawerOpen ? `${level * 20}px` : 1.5,
            py: !drawerOpen && level === 1 ? 0 : 1,
            
            ...(drawerOpen && {
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': { bgcolor: '#e6edfc', '&:hover': { bgcolor: 'transparent' } }
            }),
            ...(drawerOpen &&
              level === 1 && {
                mx: 1,
                my: 0,
                borderRadius: 1,
                '&:hover': { bgcolor: iconSelectedColor },
                '&.Mui-selected': { bgcolor: '#e6edfc', color: iconSelectedColor, '&:hover': { color: iconSelectedColor , bgcolor: '#ced9f1',} }
              }),
            ...(!drawerOpen && {
              px: 2.75,
              justifyContent: 'center',
              '&:hover': { bgcolor: 'transparent' },
              '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
            })
          }}
        >
          {itemIcon && drawerOpen && (
            <ListItemIcon
              sx={{
                position: 'relative',
                minWidth: 38,
                // color: !isSelected ? 'primary.lighter' : 'error.lighter',
                // color: 'white',
                filter: isSelected ?
                 'brightness(0) saturate(100%) invert(14%) sepia(93%) saturate(4100%) hue-rotate(209deg) brightness(92%) contrast(104%)' 
                 : 'brightness(0) saturate(100%) invert(29%) sepia(8%) saturate(629%) hue-rotate(185deg) brightness(98%) contrast(88%)',
                ...(!drawerOpen &&
                  level === 1 && {
                    borderRadius: 1,
                    width: 46,
                    height: 46,
                    alignItems: 'center',
                    justifyContent: 'center',
                    // '&:hover': { bgcolor: mode === ThemeMode.DARK ? 'secondary.light' : 'rgba(0,0,0,0.2)' }
                  }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: mode === ThemeMode.DARK ? 'secondary.100' : 'rgba(0,0,0,0.2)',
                    '&:hover': {
                      bgcolor: mode === ThemeMode.DARK ? 'secondary.200' : 'rgba(0,0,0,0.2)'
                    }
                  })
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          {itemIcon && !drawerOpen && (
            <Tooltip title={item.title} placement="right" arrow>
              <ListItemIcon
                sx={{
                  position: 'relative',
                  minWidth: 38,
                  color: !isSelected ? '$0049E0' : 'error.lighter',
                  // color: 'white',
                  filter: isSelected ? 
                  'brightness(-9) saturate(39%) invert(55%)'
                  : 'brightness(0) saturate(100%) invert(14%) sepia(93%) saturate(4100%) hue-rotate(209deg) brightness(92%) contrast(104%)' ,
                  ...(!drawerOpen &&
                    level === 1 && {
                      borderRadius: 1,
                      width: 46,
                      height: 46,
                      alignItems: 'center',
                      justifyContent: 'center',
                      // bgcolor: 'rgba(0, 73, 224, .1)',
                      '&:hover': { bgcolor: mode === ThemeMode.DARK ? 'secondary.light' : 'rgba(0,0,0,0.2)' }
                    }),
                  ...(!drawerOpen &&
                    isSelected && {
                      bgcolor: 'rgba(0, 73, 224, 1)',
                      '&:hover': {
                        bgcolor: 'rgba(0, 73, 224, 1)'
                      }
                    })
                }}
              >
                {itemIcon}
              </ListItemIcon>
            </Tooltip>
          )}

          {(drawerOpen || (!drawerOpen && level !== 1)) && (
            <ListItemText
              primary={
                <Box sx={{display: 'flex', alignItems: 'center' , gap: '10px'}}>
                  <Typography variant="h6" sx={{ color: isSelected ? '#0049E0' : '#4c525e', fontWeight: isSelected ? 600 : 400 }}>
                    {item.title}
                  </Typography>
                </Box>
              }
            />
          )}
        </ListItemButton>
      </Box>
    </>
  );
}

NavItem.propTypes = { item: PropTypes.any, level: PropTypes.number, isParents: PropTypes.bool };
